import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import * as adminService from "../services/admin";
import * as lessonsService from "../services/lessons";
import "../App.css";
import "./Admin.css";

function ColorSwatchPicker({ value, onChange, label }) {
  const inputRef = useRef(null);

  return (
    <>
      <button
        type="button"
        className="color-swatch-button"
        style={{ backgroundColor: value }}
        aria-label={label}
        title={label}
        onClick={() => inputRef.current?.click()}
      />
      <input
        ref={inputRef}
        type="color"
        className="color-swatch-input"
        value={value}
        aria-label={label}
        onChange={onChange}
      />
    </>
  );
}

const EMPTY_PACK = {
  id: null,
  name: "",
  description: "",
  subject_id: "",
  price_amount: "",
  currency: "TND",
  lesson_count: "6",
  session_length_minutes: "60",
  resources_text: "",
  image_url: "",
  recommended_for: "",
  gradient_start_color: "#E9F1FF",
  gradient_end_color: "#FFFFFF",
  offers_section: "",
  offers_kind: "offer-card",
  offers_display_order: "0",
  offers_accent: "default",
  offers_category_key: "",
  offers_title_key: "",
  offers_subtitle_key: "",
  offers_description_key: "",
  offers_price_key: "",
  offers_billing_key: "",
  offers_features_key: "",
  offers_cta_key: "",
  offers_cta_link: "",
  offers_badge_key: "",
  offers_note_key: "",
  offers_points_key: "",
  is_featured: false,
};

const OFFERS_DISPLAY_FIELDS = [
  "offers_title_key",
  "offers_subtitle_key",
  "offers_description_key",
  "offers_price_key",
  "offers_billing_key",
  "offers_features_key",
  "offers_cta_key",
  "offers_category_key",
  "offers_points_key",
  "offers_note_key",
  "offers_badge_key",
];

const OFFERS_LIST_FIELDS = new Set([
  "offers_features_key",
  "offers_points_key",
]);

function parseResources(text) {
  return String(text || "")
    .split(/\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toPackForm(pack) {
  const rawDescription = pack?.description || pack?.summary || "";

  return {
    id: pack?.id ?? null,
    name: pack?.name || "",
    description: rawDescription,
    subject_id: pack?.subject?.id ? String(pack.subject.id) : "",
    price_amount: pack?.price_amount || pack?.price?.amount || "",
    currency: "TND",
    lesson_count: pack?.lesson_count || "",
    session_length_minutes: pack?.session_length_minutes || "",
    resources_text: (pack?.resources || []).join("\n"),
    image_url: pack?.image_url || "",
    recommended_for: pack?.recommended_for || "",
    gradient_start_color: pack?.gradient_start_color || "#E9F1FF",
    gradient_end_color: pack?.gradient_end_color || "#FFFFFF",
    offers_section: pack?.offers_section || "",
    offers_kind: pack?.offers_kind || "offer-card",
    offers_display_order: String(pack?.offers_display_order ?? "0"),
    offers_accent: pack?.offers_accent || "default",
    offers_category_key: pack?.offers_category_key || "",
    offers_title_key: pack?.offers_title_key || "",
    offers_subtitle_key: pack?.offers_subtitle_key || "",
    offers_description_key: pack?.offers_description_key || "",
    offers_price_key: pack?.offers_price_key || "",
    offers_billing_key: pack?.offers_billing_key || "",
    offers_features_key: pack?.offers_features_key || "",
    offers_cta_key: pack?.offers_cta_key || "",
    offers_cta_link: pack?.offers_cta_link || "",
    offers_badge_key: pack?.offers_badge_key || "",
    offers_note_key: pack?.offers_note_key || "",
    offers_points_key: pack?.offers_points_key || "",
    is_featured: Boolean(pack?.is_featured),
  };
}

export default function PackDetails({ packId }) {
  const navigate = useNavigate();
  const params = useParams();
  const { accessToken, effectiveRole } = useAuth();
  const { t, language } = useTranslation();

  const resolvedPackId = useMemo(
    () => packId ?? params.packId,
    [packId, params.packId],
  );

  const dir = language === "ar" ? "rtl" : "ltr";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [packForm, setPackForm] = useState(EMPTY_PACK);
  const [offersDisplay, setOffersDisplay] = useState(() => {
    const initial = {};
    for (const field of OFFERS_DISPLAY_FIELDS) initial[field] = "";
    return initial;
  });

  const isAdminRole = useMemo(
    () =>
      ["admin", "superadmin", "support", "accounting"].includes(effectiveRole),
    [effectiveRole],
  );

  const cleanKey = useCallback(
    (value) => (typeof value === "string" ? value.trim() : ""),
    [],
  );

  const isOffersKey = useCallback(
    (value) => cleanKey(value).startsWith("offers."),
    [cleanKey],
  );

  const translateOrEmpty = useCallback(
    (maybeKey, options) => {
      const key = cleanKey(maybeKey);
      if (!key) return "";
      return t(key, options);
    },
    [cleanKey, t],
  );

  const buildOffersDisplay = useCallback(
    (form) => {
      const next = {};
      for (const field of OFFERS_DISPLAY_FIELDS) {
        const raw = cleanKey(form?.[field]);

        if (!raw) {
          next[field] = "";
          continue;
        }

        if (!isOffersKey(raw)) {
          next[field] = raw;
          continue;
        }

        if (OFFERS_LIST_FIELDS.has(field)) {
          const translated = translateOrEmpty(raw, { returnObjects: true });
          if (Array.isArray(translated)) {
            const text = translated.filter(Boolean).join("\n");
            next[field] = text;
          } else if (
            typeof translated === "string" &&
            translated &&
            translated !== raw
          ) {
            next[field] = translated;
          } else {
            // Hide raw key when translation is missing.
            next[field] = "";
          }
          continue;
        }

        const translated = translateOrEmpty(raw);
        next[field] = translated && translated !== raw ? translated : "";
      }
      return next;
    },
    [cleanKey, isOffersKey, translateOrEmpty],
  );

  const resolveOffersPayloadValue = useCallback(
    (field, form, display) => {
      const raw = cleanKey(form?.[field]);
      const shown = cleanKey(display);

      if (!raw) return shown;

      if (!isOffersKey(raw)) {
        return shown;
      }

      if (OFFERS_LIST_FIELDS.has(field)) {
        const translated = translateOrEmpty(raw, { returnObjects: true });
        if (Array.isArray(translated)) {
          const canonical = translated.filter(Boolean).join("\n").trim();
          if (canonical && shown === canonical) {
            return raw;
          }
        }

        // If translation is missing and user didn't type anything, keep the key.
        if (!shown) return raw;
        return shown;
      }

      const translated = translateOrEmpty(raw);
      if (typeof translated === "string") {
        const canonical = translated.trim();
        if (canonical && canonical !== raw && shown === canonical) {
          return raw;
        }
      }

      if (!shown) return raw;
      return shown;
    },
    [cleanKey, isOffersKey, translateOrEmpty],
  );

  useEffect(() => {
    if (!accessToken || !isAdminRole) {
      return;
    }

    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [catalogRes, packsRes] = await Promise.all([
          lessonsService.fetchCatalog(),
          adminService.fetchPacks(accessToken),
        ]);

        if (ignore) return;

        setSubjects(catalogRes?.subjects || []);

        const packs = packsRes?.packs || [];
        const numericId = Number(resolvedPackId);
        const found = packs.find((p) => Number(p.id) === numericId);

        if (!found) {
          setError(t("adminPage.packs.errorPackNotFound"));
          setPackForm(EMPTY_PACK);
          return;
        }

        const nextForm = toPackForm(found);
        // For offers packs we sometimes seeded `summary/description` with an i18n key.
        // In the admin form, show the translated text instead of the raw key.
        if (isOffersKey(nextForm.description)) {
          const key = cleanKey(nextForm.description);
          const translated = translateOrEmpty(key);
          nextForm.description =
            translated && translated !== key ? translated : "";
        }

        setPackForm(nextForm);
        setOffersDisplay(buildOffersDisplay(nextForm));
      } catch (e) {
        if (!ignore) {
          setError(e.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [
    accessToken,
    buildOffersDisplay,
    cleanKey,
    isAdminRole,
    isOffersKey,
    resolvedPackId,
    t,
    translateOrEmpty,
  ]);

  const backToPacks = () => {
    navigate("/packs");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!accessToken || !isAdminRole) return;

    const payload = {
      id: packForm.id,
      name: packForm.name.trim(),
      description: packForm.description.trim(),
      subject_id: packForm.subject_id ? Number(packForm.subject_id) : null,
      price_amount: packForm.price_amount ? Number(packForm.price_amount) : 0,
      currency: "TND",
      lesson_count: Number(packForm.lesson_count) || 1,
      session_length_minutes: Number(packForm.session_length_minutes) || 60,
      resources: parseResources(packForm.resources_text),
      image_url: packForm.image_url.trim(),
      recommended_for: packForm.recommended_for.trim(),
      gradient_start_color: packForm.gradient_start_color,
      gradient_end_color: packForm.gradient_end_color,
      offers_section: (packForm.offers_section || "").trim(),
      offers_kind: (packForm.offers_kind || "").trim(),
      offers_display_order: Number(packForm.offers_display_order) || 0,
      offers_accent: (packForm.offers_accent || "").trim(),
      offers_category_key: resolveOffersPayloadValue(
        "offers_category_key",
        packForm,
        offersDisplay.offers_category_key,
      ),
      offers_title_key: resolveOffersPayloadValue(
        "offers_title_key",
        packForm,
        offersDisplay.offers_title_key,
      ),
      offers_subtitle_key: resolveOffersPayloadValue(
        "offers_subtitle_key",
        packForm,
        offersDisplay.offers_subtitle_key,
      ),
      offers_description_key: resolveOffersPayloadValue(
        "offers_description_key",
        packForm,
        offersDisplay.offers_description_key,
      ),
      offers_price_key: resolveOffersPayloadValue(
        "offers_price_key",
        packForm,
        offersDisplay.offers_price_key,
      ),
      offers_billing_key: resolveOffersPayloadValue(
        "offers_billing_key",
        packForm,
        offersDisplay.offers_billing_key,
      ),
      offers_features_key: resolveOffersPayloadValue(
        "offers_features_key",
        packForm,
        offersDisplay.offers_features_key,
      ),
      offers_cta_key: resolveOffersPayloadValue(
        "offers_cta_key",
        packForm,
        offersDisplay.offers_cta_key,
      ),
      offers_cta_link: (packForm.offers_cta_link || "").trim(),
      offers_badge_key: resolveOffersPayloadValue(
        "offers_badge_key",
        packForm,
        offersDisplay.offers_badge_key,
      ),
      offers_note_key: resolveOffersPayloadValue(
        "offers_note_key",
        packForm,
        offersDisplay.offers_note_key,
      ),
      offers_points_key: resolveOffersPayloadValue(
        "offers_points_key",
        packForm,
        offersDisplay.offers_points_key,
      ),
      is_featured: Boolean(packForm.is_featured),
    };

    if (!payload.name || !payload.subject_id) {
      setError(t("adminPage.packs.errorMissingNameSubject"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await adminService.savePack(accessToken, payload);
      const nextForm = toPackForm(result.pack);
      setPackForm(nextForm);
      setOffersDisplay(buildOffersDisplay(nextForm));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdminRole) {
    return (
      <div className="admin-dashboard" dir={dir}>
        <p>{t("adminPage.accessDenied")}</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" dir={dir}>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      <section>
        <div className="admin-section-head admin-section-head--full">
          <h2 style={{ margin: 0 }}>{t("adminPage.packs.detailsTitle")}</h2>
          <button type="button" className="admin-back" onClick={backToPacks}>
            <i
              className={`fa-solid ${dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"}`}
              aria-hidden="true"
            ></i>
            <span className="sr-only">{t("adminPage.tabs.packs")}</span>
          </button>
        </div>

        {loading ? (
          <p>{t("adminPage.loading")}</p>
        ) : (
          <div className="card">
            <h3 style={{ marginTop: 0 }}>
              {packForm.name || `#${resolvedPackId}`}
            </h3>
            <form onSubmit={handleSubmit} className="form-grid">
              <label>
                {t("adminPage.packs.fields.name")}
                <input
                  required
                  name="name"
                  value={packForm.name}
                  placeholder={t("adminPage.packs.placeholders.name")}
                  onChange={(e) =>
                    setPackForm({ ...packForm, name: e.target.value })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.description")}
                <textarea
                  name="description"
                  value={packForm.description}
                  placeholder={t("adminPage.packs.placeholders.description")}
                  onChange={(e) =>
                    setPackForm({ ...packForm, description: e.target.value })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.subject")}
                <select
                  required
                  value={packForm.subject_id}
                  onChange={(e) =>
                    setPackForm({ ...packForm, subject_id: e.target.value })
                  }
                >
                  <option value="">
                    {t("adminPage.packs.fields.subjectPlaceholder")}
                  </option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="packs-form-divider" />

              <label>
                {t("adminPage.packs.fields.offersSection")}
                <select
                  value={packForm.offers_section}
                  onChange={(e) =>
                    setPackForm({ ...packForm, offers_section: e.target.value })
                  }
                >
                  <option value="">
                    {t("adminPage.packs.fields.offersSectionNone")}
                  </option>
                  <option value="class-types">class-types</option>
                  <option value="languages">languages</option>
                  <option value="upcoming">upcoming</option>
                  <option value="addons">addons</option>
                </select>
              </label>

              <label>
                {t("adminPage.packs.fields.offersKind")}
                <select
                  value={packForm.offers_kind}
                  onChange={(e) =>
                    setPackForm({ ...packForm, offers_kind: e.target.value })
                  }
                >
                  <option value="offer-card">offer-card</option>
                  <option value="addon-card">addon-card</option>
                </select>
              </label>

              <label>
                {t("adminPage.packs.fields.offersDisplayOrder")}
                <input
                  type="number"
                  min="0"
                  value={packForm.offers_display_order}
                  onChange={(e) =>
                    setPackForm({
                      ...packForm,
                      offers_display_order: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersAccent")}
                <select
                  value={packForm.offers_accent}
                  onChange={(e) =>
                    setPackForm({ ...packForm, offers_accent: e.target.value })
                  }
                >
                  <option value="default">default</option>
                  <option value="blue">blue</option>
                  <option value="teal">teal</option>
                  <option value="violet">violet</option>
                  <option value="gold">gold</option>
                  <option value="rose">rose</option>
                </select>
              </label>

              <label>
                {t("adminPage.packs.fields.offersTitleKey")}
                <input
                  value={offersDisplay.offers_title_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_title_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersSubtitleKey")}
                <input
                  value={offersDisplay.offers_subtitle_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_subtitle_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersDescriptionKey")}
                <input
                  value={offersDisplay.offers_description_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_description_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersPriceKey")}
                <input
                  value={offersDisplay.offers_price_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_price_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersBillingKey")}
                <input
                  value={offersDisplay.offers_billing_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_billing_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersFeaturesKey")}
                <textarea
                  rows={3}
                  value={offersDisplay.offers_features_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_features_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersCtaKey")}
                <input
                  value={offersDisplay.offers_cta_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_cta_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersCtaLink")}
                <input
                  value={packForm.offers_cta_link}
                  placeholder={t("adminPage.packs.placeholders.offersLink")}
                  onChange={(e) =>
                    setPackForm({
                      ...packForm,
                      offers_cta_link: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersBadgeKey")}
                <input
                  value={offersDisplay.offers_badge_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_badge_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersNoteKey")}
                <input
                  value={offersDisplay.offers_note_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_note_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersPointsKey")}
                <textarea
                  rows={3}
                  value={offersDisplay.offers_points_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_points_key: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.offersCategoryKey")}
                <input
                  value={offersDisplay.offers_category_key}
                  placeholder={t("adminPage.packs.placeholders.offersKey")}
                  onChange={(e) =>
                    setOffersDisplay({
                      ...offersDisplay,
                      offers_category_key: e.target.value,
                    })
                  }
                />
              </label>

              <div className="packs-form-divider" />

              <label>
                {t("adminPage.packs.fields.price")}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={packForm.price_amount}
                  placeholder={t("adminPage.packs.placeholders.price")}
                  onChange={(e) =>
                    setPackForm({ ...packForm, price_amount: e.target.value })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.currency")}
                <input value={packForm.currency} readOnly />
              </label>

              <label>
                {t("adminPage.packs.fields.lessons")}
                <input
                  type="number"
                  min="1"
                  value={packForm.lesson_count}
                  placeholder={t("adminPage.packs.placeholders.lessons")}
                  onChange={(e) =>
                    setPackForm({ ...packForm, lesson_count: e.target.value })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.sessionLength")}
                <input
                  type="number"
                  min="15"
                  value={packForm.session_length_minutes}
                  placeholder={t("adminPage.packs.placeholders.sessionLength")}
                  onChange={(e) =>
                    setPackForm({
                      ...packForm,
                      session_length_minutes: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.resources")}
                <textarea
                  value={packForm.resources_text}
                  onChange={(e) =>
                    setPackForm({ ...packForm, resources_text: e.target.value })
                  }
                  placeholder={t("adminPage.packs.placeholderResources")}
                />
              </label>

              <label>
                {t("adminPage.packs.fields.image")}
                <input
                  value={packForm.image_url}
                  placeholder={t("adminPage.packs.placeholders.image")}
                  onChange={(e) =>
                    setPackForm({ ...packForm, image_url: e.target.value })
                  }
                />
              </label>

              <label>
                {t("adminPage.packs.fields.recommended")}
                <input
                  value={packForm.recommended_for}
                  placeholder={t("adminPage.packs.placeholders.recommended")}
                  onChange={(e) =>
                    setPackForm({
                      ...packForm,
                      recommended_for: e.target.value,
                    })
                  }
                />
              </label>

              <div className="form-field">
                <span className="form-label">
                  {t("adminPage.packs.fields.gradientColors")}
                </span>
                <div
                  className="color-swatch-row"
                  role="group"
                  aria-label={t("adminPage.packs.fields.gradientColors")}
                >
                  <ColorSwatchPicker
                    value={packForm.gradient_start_color || "#E9F1FF"}
                    label={t("adminPage.packs.fields.gradientStart")}
                    onChange={(e) =>
                      setPackForm({
                        ...packForm,
                        gradient_start_color: e.target.value,
                      })
                    }
                  />
                  <ColorSwatchPicker
                    value={packForm.gradient_end_color || "#FFFFFF"}
                    label={t("adminPage.packs.fields.gradientEnd")}
                    onChange={(e) =>
                      setPackForm({
                        ...packForm,
                        gradient_end_color: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={packForm.is_featured}
                  onChange={(e) =>
                    setPackForm({ ...packForm, is_featured: e.target.checked })
                  }
                />
                {t("adminPage.packs.fields.featured")}
              </label>

              <div className="form-actions">
                <button type="submit" className="btn primary" disabled={saving}>
                  {saving
                    ? t("adminPage.packs.actions.saving")
                    : t("adminPage.packs.actions.save")}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
